/**
 * The client half of the takeover: glow a named element, and walk a script.
 *
 * The backend owns the script — which steps, which targets, what Zana says.
 * This file only knows how to *perform* one. It cannot invent a step, and a
 * target the page does not carry is a logged no-op rather than a silent one,
 * because a takeover that navigates and glows nothing looks broken and is
 * otherwise invisible until someone demos it.
 *
 * The state machine lives here rather than in the panel. Zana is moving someone
 * else's screen, so pausing, resuming and stopping are behaviour, not
 * decoration — the panel is a pure renderer of what this emits, which is why
 * the controls can never disagree with what is happening.
 *
 * Owner pace (Gabriel, 2026-07-31 / 2026-08-01): nothing on screen advances
 * itself. Every line parks in `waiting` (or `reading` / `holding` / `done`)
 * until Next, Back, Carry on, Yes/No, or Stop. Timers only settle the DOM
 * (~300ms) before that wait — they are not decision time.
 */

/** One numbered line inside a "take your time" read card. */
export interface TakeoverReadLine {
  k: string;
  t: string;
}

/** Expanded mid-screen summary — stays until Next / Carry on / Back. */
export interface TakeoverReadCard {
  title: string;
  lines: TakeoverReadLine[];
  secs?: number;
  /** Optional suggestion chips group (e.g. 'alerts'). */
  suggest?: string | null;
}

/** One step, exactly as `zana/engine/takeover.js` emits it. */
export interface TakeoverStep {
  say: string;
  tab: string | null;
  spotlight: string[];
  dwellMs: number;
  face: 'talking' | 'scanning' | 'alert' | 'happy';
  hold: boolean;
  confirm: string | null;
  /** Arguments for `confirm`, filled server-side from the same live figures as
   *  the narration — so the note Zana saves is about the driver she pointed at. */
  confirmParams?: Record<string, string> | null;
  /** When set, she expands the panel and waits for the owner to read. */
  read?: TakeoverReadCard | null;
}

export interface TakeoverScript {
  id: string;
  title: string;
  steps: TakeoverStep[];
  /** A jump about one bus with no better screen: open that bus (2026-09-20). */
  openBus?: string;
  /** With openBus: the bus page section to land on — an incident finding
   *  opens Incidents, the list it is about (2026-09-28). */
  openBusTab?: string;
}

export type TakeoverFace = TakeoverStep['face'] | 'thinking';

/**
 * What the panel renders.
 *
 *   driving  settling a line (tab/glow/write) — Next is ignored here so a
 *            double-click cannot skip the line she just opened
 *   waiting  line is on screen; she advances only on Next / Back
 *   reading  mid-screen summary; Carry on / Next / Back when ready
 *   paused   a human clicked the page, or hit Pause — stays until Carry on
 *   holding  a step wants a real write and is waiting for a yes or a no
 *   choosing she has asked how to work and is waiting for the answer
 *   done     finished or stopped; Next or Stop dismisses; `leaving` exits
 */
export interface TakeoverState {
  title: string;
  phase: 'choosing' | 'driving' | 'waiting' | 'reading' | 'paused' | 'holding' | 'done';
  say: string;
  face: TakeoverFace;
  /** 1-based; 0 before the first step lands. */
  step: number;
  total: number;
  /** Always 0 — kept so older panel code that reads countdown does not break. */
  countdown: number;
  /** The write this step wants, when phase is 'holding'. */
  confirm: { actionId: string; params: Record<string, string> } | null;
  /** Expanded "what's of concern" card while phase is 'reading'. */
  read: TakeoverReadCard | null;
  /** Always 0 — reading no longer auto-advances. */
  readLeft: number;
  readTotal: number;
  suggest: string | null;
  leaving: boolean;
  /** Named mini-Zana (Phase 2 agent chrome). */
  agentName?: string;
  agentMode?: 'walkthrough' | 'agent';
  /** Taskbar title = current task (not free prose stream). */
  taskTitle?: string;
  agentStatus?: 'queued' | 'working' | 'waiting_you' | 'done';
}

export interface TakeoverController {
  /** Hold position. Manual pauses wait indefinitely; movement pauses count down. */
  pause(): void;
  resume(): void;
  /** Skip the reading timer and continue the script. */
  carryOn(): void;
  stop(): void;
  /** Answer a `holding` step. `false` ends the run. */
  answerConfirm(agree: boolean): void;
  /** A click landed somewhere on the page while she was driving. */
  nudge(): void;
  /** Move on now — the owner has finished reading this line. */
  next(): void;
  /** Replay the previous line. */
  back(): void;
  /**
   * Answer the opening question: do the work alone, or walk through it.
   *
   * Asked per run rather than kept as a setting (Gabriel, 2026-08-03). Which
   * one you want is not a preference, it is a property of the moment: an office
   * with twelve alerts at 6pm wants it done, the same person looking at
   * something unfamiliar wants to watch. The person knows at that moment, so
   * that is when to ask.
   */
  choose(auto: boolean): void;
}

export const SPOTLIGHT_CLASS = 'zana-spotlight';

/** Movement pause removed — owners brush the mouse while reading. Only an
 *  explicit click (nudge) or Pause/Stop interrupts her. */
const START_GRACE_MS = 1600;
const RESUME_GRACE_MS = 1100;
/** DOM settle before we park on `waiting`. Not decision time — Next is ignored
 *  while this runs so a double-click cannot skip the new line. */
const SETTLE_MS = 300;
const DEFAULT_READ_SECS = 0;
/** Unattended pace. Long enough to watch if you happen to be looking, short
 *  enough that "just do it" means it. */
const AUTO_BEAT_MS = 850;

/** Every element currently glowing, so clearing is exact rather than a sweep. */
let lit: Element[] = [];

/** The nearest ancestor that actually scrolls, so a glow inside an overlay
 *  scrolls that overlay rather than the page behind it. */
function scrollParent(el: Element): Element | null {
  const marked = el.closest('[data-zt-scroll]');
  if (marked) return marked;
  let node = el.parentElement;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

function bringIntoView(el: Element, reduce: boolean): void {
  const behavior: ScrollBehavior = reduce ? 'auto' : 'smooth';
  const box = scrollParent(el);
  if (!box) {
    el.scrollIntoView({ block: 'center', behavior });
    return;
  }
  const er = el.getBoundingClientRect();
  const cr = box.getBoundingClientRect();
  // Only scroll when the target is near an edge — a glow that is already
  // comfortably visible should not make the page jump.
  if (er.top < cr.top + 80 || er.bottom > cr.bottom - 80) {
    box.scrollTo({ top: box.scrollTop + (er.top - cr.top) - 150, behavior });
  }
}

/**
 * Glow a set of named targets, clearing whatever was lit before.
 * Returns the ids that were NOT found, so a caller can log a dead script.
 */
export function spotlight(targetIds: string[]): string[] {
  clearSpotlight();
  const missing: string[] = [];
  const reduce = !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  for (const id of targetIds || []) {
    /* Prefer a VISIBLE match.
       `querySelector` returns the first hook in document order, and several
       names exist on more than one surface — `hrCard` sits on the dashboard
       AND on the fleet page, and High Risk itself renders as a fixed
       full-viewport overlay with the dashboard still mounted behind it. So
       first-match could pick an element that is real, glowing, and completely
       hidden underneath the screen the owner is looking at. Nothing was
       reported missing, because nothing was: it just could not be seen. */
    const candidates = Array.from(document.querySelectorAll(`[data-zt="${CSS.escape(id)}"]`));
    /* Rendered AND on top.
       A non-zero box is not enough: High Risk renders as a fixed full-viewport
       overlay with the dashboard still mounted behind it, so the dashboard's
       own copy of a hook has a perfectly good rectangle while being completely
       invisible. Hit-testing the element's centre is what distinguishes
       "occluded" from "laid out" — and `elementFromPoint` is the only thing
       in the DOM that knows about stacking contexts. */
    const onTop = (c: Element) => {
      const r = c.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return false;
      if (c.closest('[inert]') || c.closest('[aria-hidden="true"]')) return false;
      const x = Math.min(Math.max(r.left + r.width / 2, 1), window.innerWidth - 1);
      const y = Math.min(Math.max(r.top + r.height / 2, 1), window.innerHeight - 1);
      const hit = document.elementFromPoint(x, y);
      // The centre may legitimately land on a child (or on nothing, if the
      // element is scrolled out of view) — both are fine. A hit on something
      // that is neither inside nor containing it means it is covered.
      if (!hit) return true;
      return c.contains(hit) || hit.contains(c);
    };
    const el = candidates.find(onTop)
      || candidates.find((c) => {
        const r = c.getBoundingClientRect();
        return r.width > 0 && r.height > 0;
      })
      || candidates[0];
    if (!el) {
      missing.push(id);
      continue;
    }
    el.classList.add(SPOTLIGHT_CLASS);
    lit.push(el);
    bringIntoView(el, reduce);
  }
  if (missing.length) {
    // Loud on purpose. This is the failure the registry's validateRegistry()
    // test exists to prevent, and if one slips through we want it in the
    // console rather than in a customer demo.
    console.warn('[zana] spotlight targets not on this page:', missing.join(', '));
  }
  return missing;
}

/** Wait until at least one named target is in the DOM (React setState lag). */
export async function waitForSpotlightTargets(
  targetIds: string[],
  timeoutMs = 2500,
): Promise<boolean> {
  const ids = (targetIds || []).filter(Boolean);
  if (!ids.length) return true;
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (ids.some((id) => document.querySelector(`[data-zt="${CSS.escape(id)}"]`))) {
      return true;
    }
    await new Promise<void>((r) => setTimeout(r, 50));
  }
  return false;
}

export function clearSpotlight(): void {
  lit.forEach((el) => el.classList.remove(SPOTLIGHT_CLASS));
  lit = [];
}

export interface TakeoverHandlers {
  /** Switch the dashboard to a tab (or open High Risk via 'highRisk').
   *
   *  `spotlight` is what this step is about to glow. A tab is not always a
   *  screen: Analytics opens on its own Fuel sub-tab, so a step targeting
   *  `driverTable` needs the console to land on Drivers or the glow has nothing
   *  to attach to — she narrates "top of the table I have lit up" over an unlit
   *  page. The caller owns that mapping; the runner just says what it wants. */
  onTab: (tab: string, spotlight?: string[]) => void;
  /** Every state change. The panel renders this and nothing else. */
  onState: (state: TakeoverState) => void;
  /** Run a confirmed write. Any string it returns is narrated as the receipt. */
  onAct?: (actionId: string, params: Record<string, string>) => Promise<string | void>;
  /** Runs whether the script finished or was stopped. */
  onEnd: (reason: 'done' | 'stopped') => void;
  /**
   * Posted into the chat after every run that ends (auto or guided).
   *
   * It reports what was SKIPPED as well as what was done. A summary that only
   * lists successes is how people stop reading summaries — and everything this
   * skips, it skips because it needs a human.
   *
   * `changed` is true when at least one write landed — the only summaries
   * worth keeping as a receipt (an unattended run also reports "nothing for
   * me to change", which must not overwrite the last real one).
   *
   * Guided no-write runs still post (episode memory): Ask must see that a
   * walk happened even when nothing was written.
   */
  onSummary?: (text: string, meta: {
    changed: boolean;
    tabsVisited: string[];
    scriptId: string;
    title: string;
    auto: boolean;
  }) => void;
}

/**
 * Run a script. Returns the controller the panel drives.
 *
 * Pausing on movement is the whole safety story: the operator moving their
 * mouse means "I am here, wait" — she is driving their screen, so any sign of a
 * human at the wheel outranks the script. It takes real travel rather than a
 * single stray event, because 1px of jitter should not stop a walkthrough.
 */
export function runTakeover(
  script: TakeoverScript,
  handlers: TakeoverHandlers,
  opts: { auto?: boolean } = {},
): TakeoverController {
  const total = script.steps.length;
  const reduce = !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  let stopped = false;
  let state: TakeoverState = {
    title: script.title,
    phase: 'driving',
    say: 'Taking the wheel…',
    face: 'talking',
    step: 0,
    total,
    countdown: 0,
    confirm: null,
    read: null,
    readLeft: 0,
    readTotal: DEFAULT_READ_SECS,
    suggest: null,
    leaving: false,
  };

  const emit = (patch: Partial<TakeoverState>) => {
    state = { ...state, ...patch };
    handlers.onState(state);
  };
  emit({});

  /* ── pausing ───────────────────────────────────────────────────────────── */

  let graceUntil = Date.now() + START_GRACE_MS;
  let countdownTimer: ReturnType<typeof setInterval> | undefined;
  let readResolve: (() => void) | null = null;
  // Step navigation. Owners read at different speeds and sometimes want the
  // last line again; a walkthrough you cannot rewind is a video, not a tour.
  let cursor = 0;
  let rewind = false;
  let skipDwell = false;
  /** The phase a pause interrupted; Carry on restores it (see doResume). */
  let pausedFrom: 'driving' | 'waiting' = 'waiting';

  const clearCountdown = () => {
    if (countdownTimer) clearInterval(countdownTimer);
    countdownTimer = undefined;
  };

  const doResume = () => {
    if (stopped || state.phase !== 'paused') return;
    clearCountdown();
    graceUntil = Date.now() + RESUME_GRACE_MS;
    /* Resume to the phase the pause INTERRUPTED — recorded by doPause — never
     * to a hardcoded one, because both fixed choices have already wedged the
     * console once each, in opposite modes:
     *
     *   always `driving`  → guided, paused at a parked line: waitForAdvance is
     *     pending but next() ignores presses during `driving`, so Next is
     *     visible and dead (the wedge the old comment here remembered);
     *   always `waiting`  → auto, paused mid-drive: the pending wait() freezes
     *     for good (humanHasTheWheel counts `waiting`) and no waitForAdvance
     *     exists to consume Next — "Carry on" froze the run with a live-looking
     *     Next button (found by the bug-79 timer audit).
     *
     * The phase that was live is by construction one whose pending operation
     * can continue: a frozen wait() thaws under `driving`, a parked
     * waitForAdvance serves Next under `waiting`. */
    emit({ phase: pausedFrom, countdown: 0, face: 'talking' });
  };

  const finishRead = () => {
    if (!readResolve) return;
    clearCountdown();
    const done = readResolve;
    readResolve = null;
    done();
  };

  const doPause = (auto: boolean) => {
    if (stopped) return;
    if (state.phase !== 'driving' && state.phase !== 'waiting') return;
    pausedFrom = state.phase; // doResume restores exactly this — see its note.
    clearCountdown();
    // `auto` (a click on the page) and an explicit Pause now behave the same:
    // she waits. There is no longer such a thing as a pause that expires.
    void auto;
    emit({ phase: 'paused', face: 'thinking', countdown: 0 });
  };

  /* ── the run ───────────────────────────────────────────────────────────── */

  let confirmAnswer: ((result: 'yes' | 'no' | 'back') => void) | null = null;

  const teardown = (reason: 'done' | 'stopped') => {
    if (stopped) return;
    stopped = true;
    /* A write that LANDED changed the fleet's records; its report must
     * survive however the run ends. Holding receipts for Next (bug 79) opened
     * a new exit: Stop at a parked receipt tears down mid-loop, and the
     * summary used to be posted only after a clean loop exit — the record of
     * a real change silently dropped.
     *
     * Episode memory: also post on stop for guided no-write runs so Ask
     * still sees that a walk happened. */
    if (reason === 'stopped') postAutoSummary();
    clearCountdown();
    readResolve = null;
    clearSpotlight();
    handlers.onEnd(reason);
  };

  /** Dwell that does not tick down while the operator is at the wheel.
   *
   *  The test is "are we waiting on a human", not "are we driving". Those are
   *  not the same phase list, and getting it wrong wedges the whole console:
   *  the final `done` dwell would never tick down, so teardown never ran and
   *  the panel — with its full-screen click guard — stayed up for good. */
  const humanHasTheWheel = () =>
    state.phase === 'paused'
    || state.phase === 'holding'
    || state.phase === 'reading'
    || state.phase === 'waiting'
    || state.phase === 'done';
  const wait = (ms: number) =>
    new Promise<void>((resolve) => {
      let left = reduce ? Math.min(ms, 1200) : ms;
      let at = Date.now();
      const tick = () => {
        if (stopped) return resolve();
        // Back during settle should leave immediately — not sit out the glow.
        if (rewind) return resolve();
        // Do NOT honour skipDwell here — that used to let a double-clicked
        // Next burn the settle AND the following waitForAdvance in one shot,
        // which is how a line vanished before it could be read.
        const now = Date.now();
        if (!humanHasTheWheel()) left -= now - at;
        at = now;
        if (left <= 0) return resolve();
        setTimeout(tick, 60);
      };
      tick();
    });

  /** Stay on this line until Next / Back — no auto-advance. */
  const waitForAdvance = () =>
    new Promise<void>((resolve) => {
      // Park in `waiting` unless we are already on a dedicated owner-paced
      // surface (read card, confirm, or the closing/done line).
      if (
        state.phase !== 'done'
        && state.phase !== 'holding'
        && state.phase !== 'reading'
        && state.phase !== 'paused'
      ) {
        emit({
          phase: 'waiting',
          countdown: 0,
          face: state.face === 'scanning' ? 'talking' : state.face,
        });
      }
      const tick = () => {
        if (stopped) return resolve();
        // Back pressed during settle already set rewind; honour it here rather
        // than waiting for a second press.
        if (rewind) { skipDwell = false; return resolve(); }
        if (skipDwell) { skipDwell = false; return resolve(); }
        setTimeout(tick, 60);
      };
      tick();
    });

  const waitForRead = (card: TakeoverReadCard) =>
    new Promise<void>((resolve) => {
      // No auto-advance. Owners press Carry on / Next / Back when ready —
      // a countdown that moves the screen for them fights that.
      readResolve = resolve;
      clearCountdown();
      emit({
        phase: 'reading',
        read: card,
        readLeft: 0,
        readTotal: 0,
        suggest: card.suggest || null,
        countdown: 0,
        face: 'thinking',
      });
    });

  /** Where to go after this step. `back` rewinds one; everything else advances. */
  const nextIndex = (from: number) => {
    if (rewind) {
      rewind = false;
      /* Back sets skipDwell so a PENDING waitForAdvance lets go; when that
       * wait resolves, it clears both flags itself. But several paths — the
       * auto branches, the read-card continuation — consume `rewind` HERE
       * without ever passing a waitForAdvance, and a skipDwell that reaches
       * this point unconsumed is stale by definition. Left set, it
       * pre-satisfies the NEXT owner-paced wait: the adversarial review of
       * bug 79's fix traced Back-during-settle → replay → Yes → receipt gone
       * in ≤60ms — faster than the 850ms flash the fix removed. A press may
       * only ever advance the line it was aimed at. */
      skipDwell = false;
      return Math.max(0, from - 1);
    }
    return from + 1;
  };

  /** Open tab if needed, show the line, glow only after subject targets exist.
   *  Stays in `driving` (OPENING / HAS THE WHEEL) until the hop mounts — never
   *  parks on WAITING with the overview still showing and nothing lit. */
  const presentStep = async (step: TakeoverStep, stepNo: number) => {
    emit({
      say: step.say,
      face: step.face,
      step: stepNo,
      phase: 'driving',
      read: null,
      readLeft: 0,
      suggest: null,
      confirm: null,
      countdown: 0,
    });
    if (step.tab) {
      noteTab(step.tab);
      handlers.onTab(step.tab, step.spotlight);
    }
    const targets = step.spotlight.length
      ? step.spotlight
      : (step.tab === 'highRisk' ? ['alertList', 'hrCard']
        : step.tab === 'fuelReport' ? ['fuelReport', 'fuelSpend']
          : []);
    if (targets.length) {
      await waitForSpotlightTargets(targets, 2800);
      if (!stopped) spotlight(targets);
    }
  };

  /* ── how she works this run ─────────────────────────────────────────────
     `auto` does the routine work in one pass and posts a summary; guided is
     the owner-paced walkthrough that has always been here. Confirms stop and
     ask in BOTH modes — see the note at the confirm branch. */
  const auto = opts.auto === true;
  /* Three lists, not two.
     `didLines` used to collect BOTH write receipts and the lines off read
     cards, and `postAutoSummary` printed it as "Done — 5 things:". So an
     unattended run that changed absolutely nothing reported five things done,
     reciting observations back as if they were actions. `teardown` also treats
     a non-empty `didLines` as proof a write landed, so the same conflation
     made a no-op run post a summary at all. */
  const didLines: string[] = [];   // write receipts ONLY
  const sawLines: string[] = [];   // observations from read cards
  const skippedLines: string[] = [];
  const tabsVisited: string[] = [];
  let summaryPosted = false;

  const noteTab = (tab: string | null | undefined) => {
    const t = String(tab || '').trim();
    if (t && !tabsVisited.includes(t)) tabsVisited.push(t);
  };

  /** Auto's one report to the chat — what changed and what was left for the
   *  owner. Idempotent: the clean end posts it, and teardown('stopped') posts
   *  it too; whichever runs first wins.
   *
   *  Guided no-write runs ALSO post (episode memory for Ask). The old early
   *  return when `!auto && !didLines` left chat blind to walks that changed
   *  nothing — "forgot the walk". */
  const postAutoSummary = () => {
    if (summaryPosted) return;
    summaryPosted = true;
    const parts: string[] = [];
    if (didLines.length) {
      parts.push(`Done — ${didLines.length} ${didLines.length === 1 ? 'thing' : 'things'}:`);
      didLines.slice(0, 8).forEach((l) => parts.push(`• ${l}`));
      if (didLines.length > 8) parts.push(`• …and ${didLines.length - 8} more`);
    } else if (auto) {
      parts.push('I went through it and there was nothing for me to change.');
    } else {
      const where = tabsVisited.length ? tabsVisited.join(', ') : 'the desk';
      parts.push(`Walked through ${script.title || 'it'} with you (${where}) — nothing changed on the fleet.`);
    }
    // The rest is an unattended run's report of what it saw and left. Someone
    // who drove it themselves just watched all of that happen.
    if (auto && sawLines.length) {
      parts.push('');
      parts.push('What I saw:');
      sawLines.slice(0, 6).forEach((l) => parts.push(`• ${l}`));
      if (sawLines.length > 6) parts.push(`• …and ${sawLines.length - 6} more`);
    }
    if (auto && skippedLines.length) {
      parts.push('');
      parts.push(`Left for you (${skippedLines.length}):`);
      skippedLines.slice(0, 6).forEach((l) => parts.push(`• ${l}`));
    }
    handlers.onSummary?.(parts.join('\n'), {
      changed: didLines.length > 0,
      tabsVisited: [...tabsVisited],
      scriptId: script.id || '',
      title: script.title || '',
      auto,
    });
  };

  (async () => {
    try {
      emit({
        phase: 'driving',
        say: auto ? 'On it — I will report back.' : 'Taking the wheel…',
        face: auto ? 'scanning' : 'talking',
      });

      // Land on the first destination immediately (fetch already may have
      // pre-opened it; this covers topic-composed scripts without a PREOPEN).
      // Await the subject mount before any WAITING beat — topic of concern first.
      const firstNav = script.steps.find((s) => s.tab);
      if (firstNav?.tab) {
        noteTab(firstNav.tab);
        handlers.onTab(firstNav.tab, firstNav.spotlight);
        const early = firstNav.spotlight?.length
          ? firstNav.spotlight
          : (firstNav.tab === 'highRisk' ? ['alertList', 'hrCard']
            : firstNav.tab === 'fuelReport' ? ['fuelReport', 'fuelSpend']
              : []);
        if (early.length) {
          const ok = await waitForSpotlightTargets(early, 2800);
          if (ok && !stopped && cursor === 0) spotlight(early);
        }
      }

      let i = 0;
      while (i < total) {
        if (stopped) return;
        cursor = i;
        const step = script.steps[i];

        if (step.confirm) {
          /* Writes stop and ask in AUTO too, deliberately.
             "Do it all" must not become a side door around the rule that a
             model does not settle money or a child's whereabouts on its own —
             the same reason she can list parent-flagged drop-offs but cannot
             close one. Unattended mode changes the pacing of the narration, not
             who is accountable for a write. */
          // Writes keep the same posture they have everywhere else: Zana
          // proposes, the operator agrees. A refusal ends the run rather than
          // skipping the step, because the rest of it assumed the write landed.
          // Back is allowed — it is not a refusal.
          clearCountdown();
          emit({
            phase: 'holding',
            say: step.say,
            face: step.face,
            step: i + 1,
            countdown: 0,
            confirm: { actionId: step.confirm, params: step.confirmParams || {} },
            read: null,
            readLeft: 0,
            suggest: null,
          });
          if (step.tab) {
            noteTab(step.tab);
            handlers.onTab(step.tab, step.spotlight);
            await waitForSpotlightTargets(step.spotlight, 2500);
          }
          spotlight(step.spotlight);
          const result = await new Promise<'yes' | 'no' | 'back'>((resolve) => {
            confirmAnswer = resolve;
          });
          confirmAnswer = null;
          if (stopped) return;
          if (result === 'back') {
            rewind = true;
            i = nextIndex(i);
            continue;
          }
          if (result !== 'yes') {
            skippedLines.push(`${step.confirm} — you said no, so I left it`);
            return teardown('stopped');
          }

          emit({ phase: 'driving', confirm: null, say: 'On it…', face: 'scanning' });
          // The Yes just given is the freshest human signal there is; no
          // press from before it may count toward advancing its receipt.
          skipDwell = false;
          let receipt: string | void;
          try {
            receipt = await handlers.onAct?.(step.confirm, step.confirmParams || {});
          } catch (err) {
            // The owner-facing line stays vague on purpose, but swallowing the
            // cause entirely left "that didn't go through" with nothing behind
            // it — a rules denial and a network blip looked identical, in the
            // one place where the difference is the whole diagnosis.
            console.warn('[zana] takeover write failed:', step.confirm, err);
            emit({
              say: "That didn't go through — stopping here so you can check it.",
              face: 'alert',
              phase: 'waiting',
              countdown: 0,
            });
            // Owner-paced: a failure is the LAST thing that should scroll away
            // by itself.
            await waitForAdvance();
            return teardown('stopped');
          }
          if (stopped) return;
          didLines.push(typeof receipt === 'string' && receipt ? receipt : 'Done.');
          emit({
            say: typeof receipt === 'string' && receipt ? receipt : 'Done.',
            face: 'happy',
            phase: 'waiting',
            countdown: 0,
          });
          /* The receipt stays until the owner moves on — IN BOTH MODES, and
             the auto branch that used to flash it for AUTO_BEAT_MS and move on
             is deliberately gone (bug 79, Gabriel's report the evening the
             triage receipt vanished on him: "you can't even read it").

             Auto's premise — nobody is watching, receipts go to the chat
             report — is false at exactly this line. A write stops and asks in
             both modes, so whoever just clicked Yes IS watching, and what they
             are owed next is the outcome of their own decision. The failure
             path three branches up already waits in both modes for the same
             reason ("a failure is the LAST thing that should scroll away by
             itself"); success earned the same patience. The receipt still
             lands in the end-of-run chat report — this only stops the screen
             moving before a human who is provably present has pressed Next.

             A timed receipt is what hid bug 62: Zana said "2 of your buses
             have no refuel logged", the write found nothing, and the reply
             saying so was gone before it could be read. A receipt nobody
             reads is not a receipt. */
          await waitForAdvance();
          i = nextIndex(i);
          continue;
        }

        if (stopped) return;
        // Auto ("Just do it"): skip narration theater — open the tab silently
        // and jump toward the write/confirm. Guided mode still presents every
        // line. Writes still stop and ask in BOTH modes.
        if (auto && !step.confirm && !step.read) {
          /* "Skip the narration theatre" was implemented as "skip the whole
             step", which also skipped `presentStep` — the only thing that
             glows a spotlight or updates the panel's sentence and step
             counter. So an unattended run sat on "On it — I will report back."
             at step 0 with nothing highlighted, and looked frozen.
             Present the step, then move on without waiting for a human. */
          await presentStep(step, i + 1);
          if (stopped) return;
          if (step.hold && !step.confirm) skippedLines.push(step.say);
          await wait(AUTO_BEAT_MS);
          i = nextIndex(i);
          continue;
        }
        await presentStep(step, i + 1);
        if (stopped) return;

        if (step.read && step.read.lines?.length) {
          await wait(SETTLE_MS);
          if (stopped) return;
          if (auto) {
            // The read card is a "take your time" pause. Unattended, there is
            // nobody to take it — its lines go to the summary instead.
            step.read.lines.forEach((l) => sawLines.push(`${l.k} ${l.t}`.trim()));
            emit({ phase: 'driving', read: null, readLeft: 0, suggest: null, face: step.face });
            await wait(AUTO_BEAT_MS);
            i = nextIndex(i);
            continue;
          }
          await waitForRead(step.read);
          if (stopped) return;
          emit({
            phase: 'driving',
            read: null,
            readLeft: 0,
            suggest: null,
            face: step.face,
          });
          graceUntil = Date.now() + RESUME_GRACE_MS;
          i = nextIndex(i);
          continue;
        }

        // Owner-paced: settle the glow, then park until Next / Back.
        // Server dwellMs is ignored — it was paced reading time from when lines
        // auto-advanced, and must not decide anything on this screen anymore.
        await wait(SETTLE_MS);
        if (stopped) return;
        if (auto) {
          await wait(AUTO_BEAT_MS);
          if (stopped) return;
          // A closing question with nobody watching is not a question. Record
          // it as something still needing them rather than answering for them.
          if (step.hold && !step.confirm) skippedLines.push(step.say);
          i = nextIndex(i);
          continue;
        }
        await waitForAdvance();
        if (stopped) return;

        // A `hold` step with no write is her closing question — "want me to
        // flag it for the workshop?". Ending on the generic sign-off would talk
        // over it, so stop here and leave the question standing. The dock comes
        // back as she goes, which is where the chips that answer it live.
        if (step.hold && !step.confirm) {
          clearSpotlight();
          emit({ phase: 'done', face: 'happy', countdown: 0, confirm: null, read: null });
          // Her closing question stands until dismissed with "Stop — I've got
          // it", which is the one control the done phase always renders.
          await waitForAdvance();
          return teardown('done');
        }

        i = nextIndex(i);
      }
      if (stopped) return;
      clearSpotlight();
      postAutoSummary();
      emit({
        phase: 'done',
        say: auto
          ? "That's everything I could do on my own — the rest is in the chat."
          : "That's the tour — you've got the wheel back.",
        face: 'happy',
        countdown: 0,
        confirm: null,
        read: null,
      });
      await waitForAdvance();
      teardown('done');
    } catch {
      teardown('stopped');
    }
  })();

  return {
    /* The runner is past the question by the time it exists — the hook asks it
       while the script is still being composed, so this is a no-op here. */
    choose: () => {},
    pause: () => doPause(false),
    resume: doResume,
    carryOn: () => {
      if (state.phase === 'reading') finishRead();
      else doResume();
    },
    /** Advance one line. Ignored while she is still settling the glow/tab so a
     *  double-click cannot skip the line that just appeared. */
    next: () => {
      if (stopped || state.phase === 'holding') return;
      if (state.phase === 'reading') { finishRead(); return; }
      if (state.phase === 'driving') return;
      skipDwell = true;
      if (state.phase === 'paused') doResume();
    },
    /** Replay the previous line — the one they were still reading. */
    back: () => {
      if (stopped || cursor === 0) return;
      if (state.phase === 'holding') {
        rewind = true;
        if (confirmAnswer) {
          const send = confirmAnswer;
          confirmAnswer = null;
          send('back');
        }
        return;
      }
      // Back during settle is allowed — they want the previous line, not this one.
      rewind = true;
      skipDwell = true;
      if (state.phase === 'reading') finishRead();
      else if (state.phase === 'paused') doResume();
      else if (state.phase === 'driving') {
        // Settle's wait() no longer watches skipDwell; force the loop out by
        // flipping to waiting so the next waitForAdvance sees rewind.
        emit({ phase: 'waiting', countdown: 0 });
      }
    },
    nudge: () => {
      // Click on the console = interrupt → yield to chat (Guide Mode).
      // Explicit Pause remains on the panel; this is not pause-on-nudge.
      if (Date.now() < graceUntil) return;
      if (stopped) return;
      if (state.phase === 'done' || state.phase === 'choosing') return;
      if (confirmAnswer) { const reject = confirmAnswer; confirmAnswer = null; reject('no'); return; }
      stopped = true;
      /* 2026-09-17: this exit skips teardown, so it owes the chat the same
         receipt teardown('stopped') posts. A click on the page after Yes on a
         write used to end the run with the write landed and no record of it
         anywhere the next question could see. */
      // Episode on interrupt even when nothing was written.
      postAutoSummary();
      clearCountdown();
      if (readResolve) {
        const done = readResolve;
        readResolve = null;
        done();
      }
      clearSpotlight();
      emit({
        phase: 'done',
        say: "Okay — I'm back in chat. Ask me anything.",
        face: 'happy',
        countdown: 0,
        confirm: null,
        read: null,
        leaving: true,
      });
      window.setTimeout(() => {
        clearSpotlight();
        handlers.onEnd('stopped');
      }, 580);
    },
    answerConfirm: (agree: boolean) => {
      if (confirmAnswer) confirmAnswer(agree ? 'yes' : 'no');
    },
    stop: () => {
      if (stopped) return;
      if (confirmAnswer) { const reject = confirmAnswer; confirmAnswer = null; reject('no'); return; }
      // Mark stopped BEFORE resolving a read wait — otherwise the script loop
      // continues through the exit animation.
      stopped = true;
      // Same gap as nudge: Stop always owes the chat an episode.
      postAutoSummary();
      clearCountdown();
      if (readResolve) {
        const done = readResolve;
        readResolve = null;
        done();
      }
      clearSpotlight();
      emit({
        phase: 'done',
        say: "All yours — I've stopped.",
        face: 'happy',
        countdown: 0,
        confirm: null,
        read: null,
        leaving: true,
      });
      window.setTimeout(() => {
        clearSpotlight();
        handlers.onEnd('stopped');
      }, 580);
    },
  };
}

/** What to walk through: one of the named jobs, or a topic to compose from.
 *  Optional pageContext (same shape as Ask) so she skips hops the owner is
 *  already on and bends the tour toward cards/plates on screen.
 *  Optional liveCounts so High Risk / unread match the open dashboard. */
export type TakeoverPageContext = {
  page?: string;
  entityType?: string;
  entityId?: string;
  window?: string;
  cards?: { label: string; value: string }[];
  focus?: { type: string; id: string }[];
};

export type TakeoverOps = {
  busId?: string;
  plate?: string;
  createPlate?: string;
  nickname?: string;
  driverId?: string;
  driverLabel?: string;
  accountQuery?: string;
  action?: string;
};

export type TakeoverRequest =
  | {
    scriptId: string;
    ops?: TakeoverOps | null;
    pageContext?: TakeoverPageContext;
    liveCounts?: { unreadAlertCount?: number; highRiskBusCount?: number; unreadBusIds?: string[] };
    skipChooser?: boolean;
    /** With skipChooser: run unattended. Absent means the guided walk. */
    auto?: boolean;
    /** show = teach walkthrough; do = FG agent. */
    kind?: 'show' | 'do';
    walkthrough?: boolean;
  }
  | {
    topic: string;
    ops?: TakeoverOps | null;
    /** 'jump' (the default the desk asks for) lands on the subject and stops;
     *  'walk' composes the narrated walkthrough. 2026-09-18. */
    mode?: 'jump' | 'walk';
    /** The finding's own words, so the landing line is what the owner read. */
    findingTitle?: string;
    /** The finding's key (e.g. incident-cluster-KCE999Z__KCE999Z), so the
     *  engine can open the right thing — an incident finding opens the bus's
     *  Incidents tab, not the fleet-wide High Risk queue (2026-09-28). */
    findingKey?: string;
    pageContext?: TakeoverPageContext;
    liveCounts?: { unreadAlertCount?: number; highRiskBusCount?: number; unreadBusIds?: string[] };
    skipChooser?: boolean;
    /** With skipChooser: run unattended. Absent means the guided walk. */
    auto?: boolean;
    /** show = teach walkthrough; do = FG agent. */
    kind?: 'show' | 'do';
    walkthrough?: boolean;
  };

/** Fetch a resolved script from the engine. The client never sends steps. */
export async function fetchTakeover(
  apiBase: string,
  token: string,
  orgCode: string,
  req: TakeoverRequest,
): Promise<{ ok: boolean; script?: TakeoverScript; reason?: string }> {
  const res = await fetch(`${apiBase}/api/premium/zana/takeover`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ orgCode, ...req }),
  });
  if (!res.ok) return { ok: false, reason: `http_${res.status}` };
  return res.json();
}
